import React, {useState} from "react";
import {Plus, Edit, Trash2} from "lucide-react";

export interface InventoryItem {
  name: string;
  quantity: number;
  description: string;
  attunement: boolean;
}

interface InventoryFormProps {
  items: InventoryItem[];
  onItemsChange: (items: InventoryItem[]) => void;
  title?: string;
  showTable?: boolean;
  className?: string;
}

const InventoryForm: React.FC<InventoryFormProps> = ({
  items,
  onItemsChange,
  title = "Items",
  showTable = true,
  className = "",
}) => {
  const [itemForm, setItemForm] = useState<InventoryItem>({
    name: "",
    quantity: 1,
    description: "",
    attunement: false,
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<InventoryItem>({
    name: "",
    quantity: 1,
    description: "",
    attunement: false,
  });

  const handleItemFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const {name, value, type} = e.target;
    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      setItemForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (type === "number") {
      setItemForm((prev) => ({
        ...prev,
        [name]: parseFloat(value) || 0,
      }));
    } else {
      setItemForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddItem = () => {
    if (!itemForm.name || itemForm.quantity <= 0) return;

    const newItems = [...items, {...itemForm}];
    onItemsChange(newItems);

    // Reset form
    setItemForm({
      name: "",
      quantity: 1,
      description: "",
      attunement: false,
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onItemsChange(newItems);
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
    setEditingItem({...items[index]});
  };

  const handleSaveEditItem = () => {
    if (editingIndex === null) return;

    const newItems = [...items];
    newItems[editingIndex] = {...editingItem};
    onItemsChange(newItems);

    setEditingIndex(null);
  };

  const handleCancelEditItem = () => {
    setEditingIndex(null);
  };

  const handleEditingItemChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const {name, value, type} = e.target;
    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      setEditingItem((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (type === "number") {
      setEditingItem((prev) => ({
        ...prev,
        [name]: parseFloat(value) || 0,
      }));
    } else {
      setEditingItem((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  return (
    <div className={`env-inventory-form ${className}`}>
      {title && <div className="env-inventory-header">{title}</div>}

      {showTable && (
        <div className="env-inventory-content">
          <table className="env-inventory-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Quantity</th>
                <th>Description</th>
                <th>Attunement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="env-empty-inventory">
                    No items added
                  </td>
                </tr>
              ) : (
                items.map((item, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      {editingIndex === i ? (
                        <>
                          <td>
                            <input
                              name="name"
                              value={editingItem.name}
                              onChange={handleEditingItemChange}
                              className="env-inventory-form-input"
                              style={{width: "100%"}}
                            />
                          </td>
                          <td>
                            <input
                              name="quantity"
                              type="number"
                              value={editingItem.quantity}
                              onChange={handleEditingItemChange}
                              className="env-inventory-form-input"
                              style={{width: "100%"}}
                            />
                          </td>
                          <td>
                            <input
                              name="description"
                              value={editingItem.description}
                              onChange={handleEditingItemChange}
                              className="env-inventory-form-input"
                              style={{width: "100%"}}
                            />
                          </td>
                          <td>
                            <input
                              name="attunement"
                              type="checkbox"
                              checked={editingItem.attunement}
                              onChange={handleEditingItemChange}
                            />
                          </td>
                          <td>
                            <button
                              className="btn env-btn-small"
                              onClick={handleSaveEditItem}
                            >
                              Save
                            </button>
                            <button
                              className="btn"
                              onClick={handleCancelEditItem}
                            >
                              Cancel
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{item.name}</td>
                          <td>{item.quantity}</td>
                          <td>
                            <div className="env-inventory-description">
                              <span className="env-inventory-description-text">
                                {item.description}
                              </span>
                            </div>
                          </td>
                          <td>{item.attunement ? "Yes" : "No"}</td>
                          <td>
                            <button
                              className="btn env-btn-icon"
                              onClick={() => handleEditItem(i)}
                              aria-label="Edit item"
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                marginRight: 4,
                                color: "var(--main)",
                                cursor: "pointer",
                              }}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn env-btn-icon"
                              onClick={() => handleRemoveItem(i)}
                              aria-label="Remove item"
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                color: "var(--main)",
                                cursor: "pointer",
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Item Form */}
      <div className="env-inventory-form">
        <div className="env-inventory-form-row">
          <div className="env-inventory-form-field">
            <div className="env-inventory-form-label">Item name</div>
            <input
              name="name"
              value={itemForm.name}
              onChange={handleItemFormChange}
              placeholder="Item name"
              className="env-inventory-form-input"
            />
          </div>
          <div className="env-inventory-form-field-small">
            <div className="env-inventory-form-label">Quantity</div>
            <input
              name="quantity"
              type="number"
              value={itemForm.quantity}
              onChange={handleItemFormChange}
              placeholder="1"
              className="env-inventory-form-input"
            />
          </div>
          <div className="env-inventory-form-field-small">
            <div className="env-inventory-form-label">Attunement</div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "4px",
              }}
            >
              <input
                type="checkbox"
                name="attunement"
                checked={itemForm.attunement}
                onChange={handleItemFormChange}
              />
              <span>Requires attunement</span>
            </label>
          </div>
        </div>
        <div style={{marginBottom: 8}}>
          <div className="env-inventory-form-label">Description</div>
          <textarea
            name="description"
            value={itemForm.description}
            onChange={handleItemFormChange}
            placeholder="Item description"
            className="env-inventory-form-textarea"
          />
        </div>
        <button
          className="btn btn-primary env-btn-full"
          onClick={handleAddItem}
          type="button"
        >
          <Plus size={16} style={{marginRight: "8px"}} />
          Add item
        </button>
      </div>
    </div>
  );
};

export default InventoryForm;
